import { Button, DrawerFooter, VStack, Text, useToast } from "@chakra-ui/react";
import { Drawer } from "../../common";
import { updateDealership, updateUser } from "../../../utils/api/users.api";
import { useMutation } from "@tanstack/react-query";
import Loader from "../../common/Spinner";

type data = {
  isOpen: boolean;
  onClose: any;
  userId: number;
  refetchParent: any;
  isFromDealershipMenu?: boolean;
};
interface ApiResult {
  data: any;
}
interface ApiRequest {
  userId: number;
  payload: {
    user: {
      status: string;
    };
  };
}
interface ApiRequestDealership {
  userId: number;
  payload: {
    dealership: {
      status: string;
    };
  };
}
const ReActivate = (props: data) => {
  const { isOpen, onClose, userId, refetchParent, isFromDealershipMenu } =
    props;
  const toast = useToast();

  const mutation = useMutation<ApiResult, Error, ApiRequest, unknown>({
    mutationFn: async (payload: ApiRequest): Promise<ApiResult> => {
      try {
        const response = await updateUser(userId, payload.payload, {
          menu: "User",
          "Sub-Menu": "",
        });
        return { data: response.data };
      } catch (error) {
        throw error;
      }
    },
    onSuccess: () => {
      refetchParent();
      onClose();
      toast({
        description: "User re-activated successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    },
    onError: (error: any) => {
      toast({
        description: `Error re-activating User: ${
          error?.response?.data?.errors?.toString() ?? "Something went wrong"
        }`,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    },
  });
  const mutationDealership = useMutation<
    ApiResult,
    Error,
    ApiRequestDealership,
    unknown
  >({
    mutationFn: async (request: ApiRequestDealership): Promise<ApiResult> => {
      try {
        const response = await updateDealership(userId, request.payload, {
          menu: "Dealership",
          "Sub-Menu": "Settings/Users",
          "Dealership-Id": userId,
        });
        return { data: response.data };
      } catch (error) {
        throw error;
      }
    },
    onSuccess: () => {
      refetchParent();
      onClose();
      toast({
        description: "Dealership re-activated successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    },
    onError: (error: any) => {
      toast({
        description: `Error re-activating dealership: ${
          error?.response?.data?.errors?.toString() ?? "Something went wrong"
        }`,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    },
  });

  const handleSubmit = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    if (isFromDealershipMenu) {
      const requestData: ApiRequestDealership = {
        userId: userId,
        payload: {
          dealership: {
            status: "active",
          },
        },
      };

      mutationDealership.mutate(requestData);
    } else {
      const requestData: ApiRequest = {
        userId: userId,
        payload: {
          user: {
            status: "active",
          },
        },
      };

      mutation.mutate(requestData);
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={
        isFromDealershipMenu ? "Re-Activate Dealership" : "Re-Activate User"
      }
      isLoading={mutation?.isLoading}
    >
      <VStack alignItems="flex-start" padding="1rem 1.5rem" w="100%" gap="1rem">
        <Text textStyle="h4" color="var(--grey-900)" fontWeight="600">
          Are you sure you want to re-activate the{" "}
          {isFromDealershipMenu ? "dealership" : "user"}?
        </Text>
      </VStack>
      <DrawerFooter
        position="absolute"
        bottom="0"
        left="0"
        width="100%"
        borderTop="1px solid var(--grey-300)"
        display="flex"
        alignItems="center"
        gap="0.81rem"
      >
        <Button variant="outline" w="100%" size="xl" onClick={onClose}>
          Cancel
        </Button>
        <Button w="100%" size="xl" onClick={handleSubmit}>
          Confirm
        </Button>
      </DrawerFooter>

      {(mutation.isLoading || mutationDealership.isLoading) && <Loader />}
    </Drawer>
  );
};

export default ReActivate;
